import axios from "axios";

const LOYALTY_API_BASE_URL =
  process.env.REACT_APP_LOYALTY_API_BASE_URL || "https://clm-api.jagran.com/api/";

const loyaltyUrl = (path) => `${LOYALTY_API_BASE_URL}${path}`;

const appendValue = (formData, key, value) => {
  formData.append(key, value == null ? "" : String(value));
};

export const buildLoyaltyProgramFormData = ({
  id,
  productId,
  titleEn,
  titleHi,
  descriptionEn,
  descriptionHi,
  status,
  image,
  events,
}) => {
  const formData = new FormData();
  if (id) {
    appendValue(formData, "id", id);
  }
  appendValue(formData, "product_id", productId);
  appendValue(formData, "title_en", titleEn);
  appendValue(formData, "title_hi", titleHi);
  appendValue(formData, "description_en", descriptionEn);
  appendValue(formData, "description_hi", descriptionHi);
  if (image) {
    formData.append("image", image);
  }
  appendValue(formData, "status", status);

  events.forEach((event, index) => {
    if (id) {
      appendValue(formData, `event_id[${index}]`, event.eventId || event.event_id || "");
    }
    appendValue(formData, `event_key[${index}]`, event.event_key);
    appendValue(formData, `event_name_en[${index}]`, event.event_name_en);
    appendValue(formData, `event_name_hi[${index}]`, event.event_name_hi);
    appendValue(formData, `event_description_en[${index}]`, event.description_en);
    appendValue(formData, `event_description_hi[${index}]`, event.description_hi);
    appendValue(formData, `coins[${index}]`, event.coins || "0");
    appendValue(formData, `min_action[${index}]`, event.min_action || "1");
    appendValue(formData, `condition_1_key[${index}]`, event.condition_1_key);
    appendValue(formData, `condition_1[${index}]`, event.condition_1);
    appendValue(formData, `condition_1_value[${index}]`, event.condition_1_value);
    appendValue(formData, `condition_2_key[${index}]`, event.condition_2_key);
    appendValue(formData, `condition_2[${index}]`, event.condition_2);
    appendValue(formData, `condition_2_value[${index}]`, event.condition_2_value);
    appendValue(formData, `is_streak[${index}]`, event.is_streak ? "yes" : "no");
    appendValue(formData, `streak_duration[${index}]`, event.is_streak ? event.streak_duration || "0" : "0");
    appendValue(formData, `streak_coins[${index}]`, event.is_streak ? event.streak_coins || "0" : "0");
  });

  return formData;
};

export const fetchLoyaltyPrograms = async (productId) => {
  const response = await axios.post(
    loyaltyUrl("/v1/wms/loyalty/get-programs"),
    productId ? { product_id: productId } : {},
    { timeout: 15000 }
  );
  return response.data?.data?.programs || [];
};

export const fetchLoyaltyProgram = async (programId) => {
  const response = await axios.get(loyaltyUrl(`/v1/wms/loyalty/get-program/${programId}`), {
    timeout: 15000,
  });
  return response.data?.data?.program || null;
};

const formDataRequestConfig = {
  timeout: 30000,
  transformRequest: [
    (data, headers) => {
      if (typeof FormData !== "undefined" && data instanceof FormData) {
        delete headers["Content-Type"];
      }
      return data;
    },
  ],
};

export const saveLoyaltyProgram = async (formData) =>
  axios.post(loyaltyUrl("/v1/wms/loyalty/save-program"), formData, formDataRequestConfig);

export const updateLoyaltyProgram = async (formData) =>
  axios.post(loyaltyUrl("/v1/wms/loyalty/update-program"), formData, formDataRequestConfig);

export const updateLoyaltyProgramStatus = async (programId) =>
  axios.get(loyaltyUrl(`/v1/wms/loyalty/update-program-status/${programId}`), {
    timeout: 15000,
  });

export const resequenceLoyaltyEvents = async (loyaltyProgramId, eventIds) =>
  axios.post(
    loyaltyUrl("/v1/wms/loyalty/resequence-event"),
    {
      loyalty_program_id: loyaltyProgramId,
      event_ids: eventIds,
    },
    { timeout: 15000 }
  );

export const resequenceLoyaltyPrograms = async (productId, ids) =>
  axios.post(
    loyaltyUrl("/v1/wms/loyalty/resequence-program"),
    {
      product_id: productId,
      ids,
    },
    { timeout: 15000 }
  );

export const getBrands = async (productId, page = 1) => {
  const baseUrl = loyaltyUrl("/v1/wms/loyalty/get-brands/" + (productId ? `${productId}` : ""));
  const url = page ? `${baseUrl}?page=${page}` : baseUrl;

  const response = await axios.get(url, { timeout: 15000 });

  return (
    response.data?.data?.brandData || {
      dataList: [],
      currentPage: 1,
      totalPages: 1,
      totalRecords: 0,
      previousPage: null,
      nextPage: null,
    }
  );
};

export const getBrandById = async (brandId) => {
  const response = await axios.get(
    loyaltyUrl("/v1/wms/loyalty/get-brand-by-id/" + (brandId ? `${brandId}` : "")),
    { timeout: 15000 }
  );
  return response.data?.data?.brandData || {};
};

export const buildBrandFormData = ({
  id,
  productId,
  brand_name_en,
  brand_tagline_en,
  brand_name_hi,
  brand_tagline_hi,
  brand_logo,
}) => {
  const formData = new FormData();

  if (id) {
    appendValue(formData, "id", id);
  }

  if (productId) {
    appendValue(formData, "product_id", productId);
  }

  appendValue(formData, "brand_name_en", brand_name_en);
  appendValue(formData, "brand_tagline_en", brand_tagline_en);
  appendValue(formData, "brand_name_hi", brand_name_hi);
  appendValue(formData, "brand_tagline_hi", brand_tagline_hi);

  if (brand_logo && typeof brand_logo !== "string") {
    formData.append("brand_logo", brand_logo);
  } else if (brand_logo) {
    appendValue(formData, "brand_logo", brand_logo);
  }

  return formData;
};

export const saveBrand = async (formData) =>
  axios.post(loyaltyUrl("/v1/wms/loyalty/save-brand"), formData, formDataRequestConfig);

export const updateBrand = async (brandId, formData) => {
  const payload =
    formData instanceof FormData
      ? formData
      : buildBrandFormData({
          id: brandId,
          ...formData,
        });

  if (brandId && !payload.has("id")) {
    appendValue(payload, "id", brandId);
  }

  return axios.post(
    loyaltyUrl("/v1/wms/loyalty/update-brand"),
    payload,
    formDataRequestConfig
  );
};

export const updateBrandStatus = async (brandId) => {
  return axios.get(
    loyaltyUrl("/v1/wms/loyalty/update-brand-status/" + (brandId ? `${brandId}` : "")),
    { timeout: 15000 }
  );
}

export const getoffersbybrand = async (brandId, page = 1) => {
  const baseUrl = loyaltyUrl("/v1/wms/loyalty/get-brand-offer/" + (brandId ? `${brandId}` : ""));
  const url = page ? `${baseUrl}?page=${page}` : baseUrl;

  const response = await axios.get(url, { timeout: 15000 });

  return (
    response.data?.data?.offers || {
      brand: null,
      dataList: [],
      currentPage: 1,
      totalPages: 1,
      totalRecords: 0,
      previousPage: null,
      nextPage: null,
    }
  );
};

export const getofferbyid = async (offerId) => {
  const response = await axios.get(
    loyaltyUrl("/v1/wms/loyalty/get-offer-by-id/" + (offerId ? `${offerId}` : "")),
    { timeout: 15000 }
  );
  return response.data?.data?.offerData || {};
};

export const buildOfferFormData = ({
  id,
  brandId,
  offer_title_en,
  offer_title_hi,
  offer_tagline_en,
  offer_tagline_hi,
  offer_description_en,
  offer_description_hi,
  offer_banner,
  redirection_url,
  save_amount,
  start_date,
  end_date,
  redemption_price,
  redemption_type,
}) => {
  const formData = new FormData();

  if (id) {
    appendValue(formData, "id", id);
  }

  if (brandId) {
    appendValue(formData, "brand_id", brandId);
  }

  appendValue(formData, "offer_title_en", offer_title_en);
  appendValue(formData, "offer_title_hi", offer_title_hi);
  appendValue(formData, "offer_tagline_en", offer_tagline_en);
  appendValue(formData, "offer_tagline_hi", offer_tagline_hi);
  appendValue(formData, "offer_description_en", offer_description_en);
  appendValue(formData, "offer_description_hi", offer_description_hi);
  appendValue(formData, "redirection_url", redirection_url);
  appendValue(formData, "save_amount", save_amount);
  appendValue(formData, "start_date", start_date);
  appendValue(formData, "end_date", end_date);
  appendValue(formData, "redemption_price", redemption_price);
  appendValue(formData, "redemption_type", redemption_type);

  if (offer_banner && typeof offer_banner !== "string") {
    formData.append("offer_banner", offer_banner);
  } else if (offer_banner) {
    appendValue(formData, "offer_banner", offer_banner);
  }

  return formData;
};

export const saveBrandOffer = async (brandId, formData) => {
  const payload =
    formData instanceof FormData
      ? formData
      : buildOfferFormData({
          brandId,
          ...formData,
        });

  if (brandId && !payload.has("brand_id")) {
    appendValue(payload, "brand_id", brandId);
  }

  return axios.post(
    loyaltyUrl("/v1/wms/loyalty/save-brand-offer"),
    payload,
    formDataRequestConfig
  );
};

export const updateOfferByBrand = async (brandId, formData) => {
  const payload =
    formData instanceof FormData
      ? formData
      : buildOfferFormData({
          brandId,
          ...formData,
        });

  if (brandId && !payload.has("brand_id")) {
    appendValue(payload, "brand_id", brandId);
  }

  return axios.post(
    loyaltyUrl("/v1/wms/loyalty/update-brand-offer"),
    payload,
    formDataRequestConfig
  );
};

export const updateOfferFeaturedStatus = async (offerId) => {
  return axios.get(
    loyaltyUrl("/v1/wms/loyalty/update-brand-offer-feature/" + (offerId ? `${offerId}` : "")),
    { timeout: 15000 }
  );
}

export const updateBrandOfferStatus = async (offerId) => {
  return axios.get(
    loyaltyUrl("/v1/wms/loyalty/update-brand-offer-status/" + (offerId ? `${offerId}` : "")),
    { timeout: 15000 }
  );
}

export const getCouponsByOffer = async (offerId, page = 1) => {
  const url =
    loyaltyUrl("/v1/wms/loyalty/get-offer-coupons/" + (offerId ? `${offerId}` : "")) +
    (page ? `?page=${page}` : "");

  const response = await axios.get(url, { timeout: 15000 });
  return (
    response.data?.data?.coupons || {
      offer: null,
      dataList: [],
      currentPage: 1,
      totalPages: 1,
      totalRecords: 0,
      previousPage: null,
      nextPage: null,
    }
  );
};

export const updateOfferCoupon= async (payload) => {
  return axios.post(
    loyaltyUrl("/v1/wms/loyalty/update-offer-coupon"),
    payload,
    { timeout: 15000 }
  );
}

export const saveOfferCoupon= async (payload) => {
  return axios.post(
    loyaltyUrl("/v1/wms/loyalty/save-offer-coupon"),
    payload,
    { timeout: 15000 }
  );
}

export const updateOfferCouponStatus = async (couponId) => {
  return axios.get(
    loyaltyUrl("/v1/wms/loyalty/update-offer-coupon-status/" + (couponId ? `${couponId}` : "")),
    { timeout: 15000 }
  );
}

const loyaltyService = {
  fetchLoyaltyPrograms,
  fetchLoyaltyProgram,
  saveLoyaltyProgram,
  updateLoyaltyProgram,
  updateLoyaltyProgramStatus,
  resequenceLoyaltyEvents,
  resequenceLoyaltyPrograms,
  buildLoyaltyProgramFormData,
  buildBrandFormData,
  buildOfferFormData,
  saveBrand,
  saveBrandOffer,
  getBrands,
  getBrandById,
  updateBrand,
  updateOfferByBrand,
  getCouponsByOffer,
};

export default loyaltyService;
